import type { BriefCleanupResponse } from '../live/api.ts';

/**
 * What a finished Brief Clean Up does to the local draft.
 *
 * Clean Up is deliberately an unsaved-draft operation: nothing here writes the ticket, and the
 * stored brief moves only when the operator presses Save brief. This decision is kept out of
 * the component so the whole visible workflow -- applied, unchanged, failed, cancelled -- can be
 * regression-tested without driving a browser.
 */
export type BriefCleanupDecision =
  /** The poll has not finished; keep waiting and show the progress wording. */
  | { kind: 'pending'; state: 'queued' | 'processing'; message: string }
  /** The draft the operator submitted is no longer on screen. Leave everything alone. */
  | { kind: 'discard' }
  /** Nothing to apply, with the reason to print. The draft is untouched. */
  | { kind: 'message'; tone: 'info' | 'error'; message: string }
  /** Apply these fields to the local draft. `acceptance` is undefined when it must not move. */
  | { kind: 'apply'; problem: string; acceptance?: string; message: string };

const PROGRESS: Record<'queued' | 'processing', string> = {
  queued: 'Queued for a runner…',
  processing: 'A runner is cleaning up this draft…',
};

export function decideBriefCleanup({
  status,
  submittedProblem,
  submittedAcceptance,
  currentProblem,
  currentAcceptance,
}: {
  status: Pick<BriefCleanupResponse, 'status' | 'result' | 'error'>;
  /** The draft as it was when Clean Up was pressed. */
  submittedProblem: string;
  submittedAcceptance: string;
  /** The draft as it is now, which the operator may have typed into while the job ran. */
  currentProblem: string;
  currentAcceptance: string;
}): BriefCleanupDecision {
  if (status.status === 'queued' || status.status === 'processing') {
    return { kind: 'pending', state: status.status, message: PROGRESS[status.status] };
  }

  // Anything typed since submission wins: applying a result computed from older text would
  // silently discard the operator's own edit.
  if (currentProblem !== submittedProblem || currentAcceptance !== submittedAcceptance) {
    return { kind: 'discard' };
  }

  if (status.status !== 'succeeded' || !status.result) {
    return {
      kind: 'message',
      tone: 'error',
      message: status.error ?? 'Clean Up did not complete. Your draft is unchanged.',
    };
  }

  if (status.result.outcome === 'unchanged') {
    return {
      kind: 'message',
      tone: 'info',
      message: 'No confident reorganisation was found. Your draft is unchanged.',
    };
  }

  const message = 'Cleaned draft applied locally. Review and edit it before saving.';
  // Acceptance is only ever populated, never rewritten: a criterion the operator already wrote
  // is the contract, and a model is not allowed to reword it behind their back.
  const mayFillAcceptance =
    submittedAcceptance.trim().length === 0 &&
    currentAcceptance.trim().length === 0 &&
    status.result.acceptanceCriteria !== null;

  return mayFillAcceptance
    ? { kind: 'apply', problem: status.result.problem, acceptance: status.result.acceptanceCriteria as string, message }
    : { kind: 'apply', problem: status.result.problem, message };
}
